import { useState } from "react";
import { Navbar } from "react-bootstrap";
import { PHASE } from "../../../../../common/constants.ts";
import { useLocalPartial } from "../../util/local.ts";
import { useViewData } from "../../util/viewManager.tsx";
import LogoAndText from "../LogoAndText.tsx";
import { PhaseBarSheet } from "../PhaseBarSheet.tsx";
import i18n from "../../util/i18n.ts";
import { translatePhaseText } from "../../util/phaseText.ts";

// The top bar's first line already carries the phase name. Rather than repeat a
// flat "Idle" on the second line, phrase it as the GM's current focus for the
// phase — a bit of game feel without duplicating line 1 (法则二, plan G1c).
const idlePhaseText = (phase: number): string => {
	switch (phase) {
		case PHASE.PRESEASON:
			return i18n.t("navBar.idlePhase.preseason", {
				ns: "ui",
				defaultValue: "Preparing for the season",
			});
		case PHASE.REGULAR_SEASON:
		case PHASE.AFTER_TRADE_DEADLINE:
			return i18n.t("navBar.idlePhase.regularSeason", {
				ns: "ui",
				defaultValue: "The grind is on",
			});
		case PHASE.PLAYOFFS:
			return i18n.t("navBar.idlePhase.playoffs", {
				ns: "ui",
				defaultValue: "Chasing the title",
			});
		case PHASE.DRAFT_LOTTERY:
			return i18n.t("navBar.idlePhase.draftLottery", {
				ns: "ui",
				defaultValue: "The offseason begins",
			});
		case PHASE.DRAFT:
			return i18n.t("navBar.idlePhase.draft", {
				ns: "ui",
				defaultValue: "Scouting prospects",
			});
		case PHASE.AFTER_DRAFT:
			return i18n.t("navBar.idlePhase.afterDraft", {
				ns: "ui",
				defaultValue: "Welcoming rookies",
			});
		case PHASE.RESIGN_PLAYERS:
			return i18n.t("navBar.idlePhase.resignPlayers", {
				ns: "ui",
				defaultValue: "Locking up your core",
			});
		case PHASE.FREE_AGENCY:
			return i18n.t("navBar.idlePhase.freeAgency", {
				ns: "ui",
				defaultValue: "Working the market",
			});
		default:
			return i18n.t("navBar.idle", { ns: "ui", defaultValue: "Standing by" });
	}
};

// Translate known static status strings set by the server (updateStatus.ts).
// "Idle" becomes a phase-aware focus phrase (see idlePhaseText). Dynamic strings
// (e.g. "3 days left") are left as-is.
const translateStatusText = (text: string, phase: number): string => {
	if (text === "Idle") {
		return idlePhaseText(phase);
	}
	const known: Record<string, string> = {
		"You're fired!": i18n.t("navBar.youreFired", {
			ns: "ui",
			defaultValue: "You're fired!",
		}),
		"Your team folded!": i18n.t("navBar.teamFolded", {
			ns: "ui",
			defaultValue: "Your team folded!",
		}),
		"Draft in progress...": i18n.t("navBar.draftInProgress", {
			ns: "ui",
			defaultValue: "Draft in progress...",
		}),
	};
	return known[text] ?? text;
};

const PhaseStatusBlock = () => {
	const { liveGameInProgress, phase, phaseText, playMenuOptions, statusText } =
		useLocalPartial([
			"liveGameInProgress",
			"phase",
			"phaseText",
			"playMenuOptions",
			"statusText",
		]);
	// G6: the phase banner is now a button that opens the phase "time bar" sheet
	// (PhaseBarSheet). Sheet open/close is pure component state — it changes no URL
	// and pushes no history entry (MOBILE_SHEET_CONTRACT_V1, inherited from
	// MobileSheet). The single CTA inside the sheet carries the old banner link's
	// navigation to the current phase route.
	const [sheetOpen, setSheetOpen] = useState(false);

	// Hide phase and status, to prevent revealing that the playoffs has ended, thus spoiling a 3-0/3-1/3-2 finals
	// game. This is needed because game sim happens before the results are displayed in liveGame.
	const text = (
		<>
			{liveGameInProgress
				? i18n.t("navBar.liveGame", { ns: "ui", defaultValue: "Live game" })
				: translatePhaseText(phaseText)}
			<br />
			{liveGameInProgress
				? i18n.t("navBar.inProgress", { ns: "ui", defaultValue: "in progress" })
				: translateStatusText(statusText, phase)}
		</>
	);

	return (
		<div className="dropdown-links navbar-nav flex-shrink-1 overflow-hidden text-nowrap">
			<div className="nav-item">
				{/* G6: same two-line visual + height as the old banner link; only the
				    element type (a→button) and click behaviour change. The native
				    button chrome is reset so the layout/height is unchanged. */}
				<button
					type="button"
					aria-expanded={sheetOpen}
					aria-haspopup="dialog"
					aria-label={i18n.t("phaseBar.openLabel", {
						ns: "ui",
						defaultValue: "Season progress",
					})}
					className="nav-link text-start"
					data-phase-bar-trigger=""
					onClick={() => {
						setSheetOpen(true);
					}}
					style={{
						background: "transparent",
						border: 0,
						lineHeight: 1.35,
						padding: "9px 0 8px 16px",
					}}
				>
					{text}
				</button>
			</div>
			<PhaseBarSheet
				onClose={() => {
					setSheetOpen(false);
				}}
				open={sheetOpen}
				phase={phase}
				phaseText={phaseText}
				playMenuOptionIds={playMenuOptions.map((option) => option.id)}
			/>
		</div>
	);
};

export const MobileTopBar = ({ updating }: { updating: boolean }) => {
	const { lid } = useLocalPartial(["lid"]);
	const viewInfo = useViewData();

	// Checking lid too helps with some flicker
	const inLeague = viewInfo?.inLeague && lid !== undefined;

	return (
		<Navbar
			bg="light"
			expand="sm"
			fixed="top"
			className="navbar-border flex-nowrap"
			data-mobile-app-bar=""
		>
			<div className="container-fluid">
				<LogoAndText inLeague={inLeague} updating={updating} />
				{inLeague ? <PhaseStatusBlock /> : null}
				<div className="flex-grow-1" />
			</div>
		</Navbar>
	);
};
